// Centralized navigation configuration for Facility Console drawer

// Facility section items with route builders
export const FACILITY_ITEMS = [
  { 
    key: 'species', 
    label: 'Species',
    buildHref: (facilityId) => `/facilities/${facilityId}/species` 
  },
  { 
    key: 'animals', 
    label: 'Animals',
    buildHref: (facilityId) => `/facilities/${facilityId}/animals` 
  },
  {
    key: 'clutch',
    label: 'Clutch',
    buildHref: (facilityId) => `/facilities/${facilityId}/clutch`
  },
  // I-03 R4 · S3-04.2 — who bred with whom. NOT managerOnly: recording a
  // pairing is the daily work of whoever watched it happen (the API's own
  // tier), and only the DELETE is Owner/Manager.
  {
    key: 'pairings',
    label: 'Pairings',
    buildHref: (facilityId) => `/facilities/${facilityId}/pairings`,
  },
  { 
    key: 'racks', 
    label: 'Racks/Enclosures',
    buildHref: (facilityId) => `/facilities/${facilityId}/racks-enclosures` 
  },
];

// Task System section items (Phase C)
// Grouped: Tasks → Feeding → Misc
// Section title: "Tasks & Feedings" (set in FacilityConsoleDrawer)
// Items with managerOnly: true are hidden from Staff/Employee users
export const TASK_ITEMS = [
  // ── Tasks ──
  {
    key: 'tasksToday',
    label: 'Tasks Today',
    buildHref: (facilityId) => `/facilities/${facilityId}/tasks/today`
  },
  {
    key: 'tasksAll',
    label: 'All Tasks',
    buildHref: (facilityId) => `/facilities/${facilityId}/tasks/all`,
    managerOnly: true
  },
  // R5 B4 (desktop standard, rule 6): Tasks is three tabs — Today | All
  // tasks | Setup — and the nav names the same three. Setup is ONE page
  // with the Templates and Schedules sections; their old routes
  // (`/tasks/templates`, `/tasks/schedules`) redirect to it, anchored.
  {
    key: 'taskSetup',
    label: 'Task Setup',
    buildHref: (facilityId) => `/facilities/${facilityId}/tasks/setup`,
    managerOnly: true
  },
  // ── Feeding ──
  // I-03 S3-07.1 (Fable ruling 6): ONE "Feeding" page with tabs — Plan,
  // History, Foods, Profiles — replaced the four entries that used to sit
  // here (Feeding History, Feeding Profiles, Feeding Plan, Feeder Status).
  // NOT managerOnly: History and Foods are any facility role; the page
  // itself hides the Plan and Profiles tabs from Staff.
  {
    key: 'feeding',
    label: 'Feeding',
    buildHref: (facilityId) => `/facilities/${facilityId}/feeding`
  },
  // R7-INVENTORY slice 1 (spec §7) — what the facility keeps a count of.
  // NOT managerOnly: any role reads, counts and flags; the page itself
  // hides the Purchases tab and every price from Staff.
  {
    key: 'supplies',
    label: 'Supplies',
    buildHref: (facilityId) => `/facilities/${facilityId}/supplies`
  },
  // ── Misc ──
  {
    key: 'pipelineHistory',
    label: 'Pipeline History',
    buildHref: (facilityId) => `/facilities/${facilityId}/pipeline`,
    managerOnly: true
  },
  {
    key: 'employeeSchedules',
    label: 'Employee Schedules',
    buildHref: (facilityId) => `/facilities/${facilityId}/employee-schedules`,
    managerOnly: true
  },
  // R-TRAINING slice 1 (§9.1, §9.3) — the training register and Froggy U.
  // NOT managerOnly: a Staff keeper reads their own row on Training (the
  // server sends them nothing else), and Froggy U is every keeper's own
  // day; a junior lands there after signing in.
  {
    key: 'training',
    label: 'Training',
    buildHref: (facilityId) => `/facilities/${facilityId}/training`
  },
  {
    key: 'froggyU',
    label: 'Froggy U',
    buildHref: (facilityId) => `/facilities/${facilityId}/froggy-u`
  },
  // I-03 R3 / S3-03.4 — the events catalog admin: what can be logged,
  // the routines that log several things at once, and the rules that
  // turn a log into a task. It sits in this section because the rules
  // are where tasks come from; the LOGGING itself has no page of its
  // own by design (proposal §4: two taps from wherever the animal or
  // the tank already is).
  //
  // NOT managerOnly: reading the catalog is any facility role (the
  // backend gates the WRITES, and the page hides every write control
  // for Staff), and "what can I log, and why did this task appear?" is
  // a question Staff ask more often than an Owner does.
  {
    key: 'eventsSettings',
    label: 'Events',
    buildHref: (facilityId) => `/facilities/${facilityId}/events-settings`
  },
  // R-NOTIFY §5.5 — Settings → Notifications: the morning email's hour
  // (Owner/Manager edit) and each person's own "Email me" switches. Not
  // managerOnly: a Staff account reads the server's line there.
  {
    key: 'notificationSettings',
    label: 'Notifications',
    buildHref: (facilityId) => `/facilities/${facilityId}/notification-settings`
  },
];

// AI section items with route builders
export const AI_ITEMS = [
  {
    key: 'globalContext',
    // Renamed from 'Global Context' (2026-08-03 consolidation): the page is
    // now the AI Context hub. Route + key unchanged to avoid breaking links.
    label: 'AI Context',
    buildHref: (facilityId) => `/facilities/${facilityId}/ai-context`
  },
  {
    key: 'froggerAI',
    label: 'FroggerAI 1.2',
    buildHref: (facilityId) => `/facilities/${facilityId}/frogger-ai`
  },
];

// Store section items with route builders
export const STORE_ITEMS = [
  { 
    key: 'manageProducts', 
    label: 'Manage Products',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/manage-products` 
  },
  {
    key: 'cuttings',
    label: 'Plant Cuttings',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/cuttings`,
    managerOnly: true,
  },
  { 
    key: 'images', 
    label: 'Media',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/images` 
  },
  { 
    key: 'shippingSettings', 
    label: 'Shipping Settings',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/shipping-settings` 
  },
  { 
    key: 'shippingOrders', 
    label: 'Shipping & Orders',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/shipping-orders` 
  },
  { 
    key: 'salesHistory', 
    label: 'Sales History',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/sales-history` 
  },
  {
    key: 'promotions',
    label: 'Promotions',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/promotions`
  },
  {
    key: 'marketing',
    label: 'Marketing',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/marketing`,
    managerOnly: true,
  },
  {
    key: 'marketingChat',
    label: 'Marketing AI Chat',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/marketing/chat`,
    managerOnly: true,
  },
  {
    key: 'operatorTelemetry',
    label: 'Operator Telemetry',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/operator-telemetry`,
    managerOnly: true,
  },
  {
    key: 'businessHealth',
    label: 'Business Health',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/business-health`,
    managerOnly: true,
  },
  {
    key: 'recentActivity',
    label: 'Recent Activity',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/recent-activity`,
    managerOnly: true,
  },
  {
    key: 'clientJourney',
    label: 'Client Journey',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/client-journey`,
    managerOnly: true,
  },
  {
    key: 'pipelineStatus',
    label: 'Nightly Pipeline',
    buildHref: (storeId, facilityId) => `/my-store/${facilityId}/pipeline-status`,
    managerOnly: true,
  },
];

// Helper to get all navigation items
export const getAllNavigationItems = () => ({
  facilities: FACILITY_ITEMS,
  tasks: TASK_ITEMS,
  ai: AI_ITEMS,
  stores: STORE_ITEMS
});

// Helper to build href for a specific item
export const buildItemHref = (section, key, facilityId, storeId = null) => {
  if (section === 'facilities') {
    const item = FACILITY_ITEMS.find(i => i.key === key);
    return item ? item.buildHref(facilityId) : null;
  } else if (section === 'tasks') {
    const item = TASK_ITEMS.find(i => i.key === key);
    return item ? item.buildHref(facilityId) : null;
  } else if (section === 'ai') {
    const item = AI_ITEMS.find(i => i.key === key);
    return item ? item.buildHref(facilityId) : null;
  } else if (section === 'stores') {
    const item = STORE_ITEMS.find(i => i.key === key);
    return item ? item.buildHref(storeId, facilityId) : null;
  }
  return null;
};